import { Module } from "../../ui/Module.js";
import { i18n } from "../../globalization/i18n.js";
import { emailAddressValidation } from "../../data/model/data-validations.js";

export class AccountModule extends Module {

    static MODE_REGISTRATION = 'registration';
    static MODE_REGISTRATION_SUCCESS = 'registration-success';
    static MODE_PASSWORD_RESET_START = 'password-reset-start';
    static MODE_PASSWORD_RESET_START_SUCCESS = 'password-reset-start-success';
    static MODE_PASSWORD_RESET = 'password-reset';
    static MODE_PASSWORD_RESET_SUCCESS = 'password-reset-success';
    static MODE_EMAIL_VERIFICATION = 'email-verification';
    static MODE_EMAIL_VERIFICATION_SUCCESS = 'email-verification-success';
    static MODE_ACCOUNT_DETAILS = 'account-details';

    static ROUTE_REGISTRATION = 'account-registration';
    static ROUTE_REGISTRATION_SUCCESS = 'account-registration-success';
    static ROUTE_PASSWORD_RESET_START = 'account-password-reset-start';
    static ROUTE_PASSWORD_RESET_START_SUCCESS = 'account-password-reset-start-success';
    static ROUTE_PASSWORD_RESET = 'account-password-reset';
    static ROUTE_PASSWORD_RESET_SUCCESS = 'account-password-reset-success';
    static ROUTE_EMAIL_VERIFICATION = 'account-email-verification';
    static ROUTE_EMAIL_VERIFICATION_SUCCESS = 'account-email-verification-success';
    static ROUTE_ACCOUNT_DETAILS = 'account-account-details';

    constructor(app, params = {}) {

        super(app, params);

        this.viewMode = AccountModule.MODE_REGISTRATION;

        this.moduleName = 'AccountModule';
        this.moduleTitle = i18n.t('entity.account.label');

        this.loginData = this.createLoginData();
        this.registerData = this.createRegisterData();
        this.emailVerificationData = this.createEmailVerificationData();
        this.passwordChangeData = this.createPasswordChangeData();
        this.passwordResetStartData = this.createPasswordResetStartData();
        this.passwordResetData = this.createPasswordResetData();
        this.userData = this.createUserData(this);
        this.accountData = this.createAccountData(this);

        this.accountService = null;

    };

    // --------------------------------------------------------------------------------------------

    static VIEWMODES_ROUTES = [
        {
            mode: AccountModule.MODE_REGISTRATION,
            route: AccountModule.ROUTE_REGISTRATION,
        },
        {
            mode: AccountModule.MODE_REGISTRATION_SUCCESS,
            route: AccountModule.ROUTE_REGISTRATION_SUCCESS,
            initializer: (module, options, activeRoute) => {

                module.registerData = module.createRegisterData();

            },
        },
        {
            mode: AccountModule.MODE_PASSWORD_RESET_START,
            route: AccountModule.ROUTE_PASSWORD_RESET_START,
            initializer: (module, options, activeRoute) => {

                module.passwordResetStartData = module.createPasswordResetStartData();

            },
        },
        {
            mode: AccountModule.MODE_PASSWORD_RESET_START_SUCCESS,
            route: AccountModule.ROUTE_PASSWORD_RESET_START_SUCCESS,
            initializer: (module, options, activeRoute) => {

                module.passwordResetStartData = module.createPasswordResetStartData();

            },
        },
        {
            mode: AccountModule.MODE_PASSWORD_RESET,
            route: AccountModule.ROUTE_PASSWORD_RESET,
            initializer: (module, options, activeRoute) => {

                const query = activeRoute.query;

                module.passwordResetData = module.createPasswordResetData();
                module.passwordResetData.code = (query && query.code) || null;

            }
        },
        {
            mode: AccountModule.MODE_PASSWORD_RESET_SUCCESS,
            route: AccountModule.ROUTE_PASSWORD_RESET_SUCCESS,
        },
        {
            mode: AccountModule.MODE_EMAIL_VERIFICATION,
            route: AccountModule.ROUTE_EMAIL_VERIFICATION,
            initializer: (module, options, activeRoute) => {

                const query = activeRoute.query;

                module.emailVerificationData = module.createEmailVerificationData();
                module.emailVerificationData.code = (query && query.code) || null;

            },
        },
        {
            mode: AccountModule.MODE_EMAIL_VERIFICATION_SUCCESS,
            route: AccountModule.ROUTE_EMAIL_VERIFICATION_SUCCESS,
            initializer: (module, options, activeRoute) => {

                module.emailVerificationData = module.createEmailVerificationData();

            },
        },
        {
            mode: AccountModule.MODE_ACCOUNT_DETAILS,
            route: AccountModule.ROUTE_ACCOUNT_DETAILS,
        },
    ];

    getRouteDescriptor(route) {

        return AccountModule.VIEWMODES_ROUTES.find((item) => item.route == route);

    };

    getRouteViewMode(route) {

        let found = this.getRouteDescriptor(route);

        if (found)
            return found.mode;

        return AccountModule.MODE_REGISTRATION;

    };

    getViewModeRoute(mode) {

        let found = AccountModule.VIEWMODES_ROUTES.find((item) => item.mode == mode);

        if (found)
            return found.route;

        return 'account-registration';

    };

    getViewModeUrl(mode) {

        return this.getApp().getRouteHash(this.getViewModeRoute(mode));

    };

    // --------------------------------------------------------------------------------------------  

    createLoginData() {

        return {
            username: null,
            password: null,
        };

    };

    createRegisterData() {

        return {
            username: null,
            email: null,
            password: null,
            passwordVerification: null,
            firstName: null,
            lastName: null,
        };

    };

    createEmailVerificationData() {

        return {
            code: null
        };

    };

    createPasswordChangeData() {

        return {
            actualPassword: null,
            password: null,
            passwordVerification: null,
        };

    };

    createPasswordResetStartData() {

        return {
            email: null
        };

    };

    createPasswordResetData() {

        return {
            code: null,
            password: null,
            passwordVerification: null,
        };

    };

    createUserData(module) {

        const authManager = this.getAuthManager();
        const loggedUser = authManager.getUser();

        if (!loggedUser) {
            return {
                username: null,
                email: null,
            };
        }

        return {
            username: loggedUser.username,
            email: loggedUser.email,
        };

    };

    createAccountData(module) {

        const authManager = this.getAuthManager();
        const loggedUser = authManager.getUser();

        if (!loggedUser) {
            return {
                firstName: null,
                lastName: null,
            };
        }

        return {
            firstName: loggedUser.firstName,
            lastName: loggedUser.lastName,
        };

    };

    // --------------------------------------------------------------------------------------------

    isLoginDataValid() {

        let data = this.loginData || {};

        return (data.username && data.password);

    };

    isRegisterDataValid() {

        let data = this.registerData || {};

        return (
            !!data.username
            && !!data.password
            && data.password == data.passwordVerification
            && !!data.email
            && emailAddressValidation.validate(data.email)
        );

    };

    isEmailVerificationDataValid() {

        let data = this.emailVerificationData || {};

        return (
            !!data.code
        );

    };

    isPasswordChangeDataValid() {

        let data = this.passwordChangeData || {};

        return (
            !!data.actualPassword
            && !!data.password
            && !!data.passwordVerification
            && data.password == data.passwordVerification
        );

    };

    isPasswordResetStartDataValid() {

        let data = this.passwordResetStartData || {};

        return (
            !!data.email
            && emailAddressValidation.validate(data.email)
        );

    };

    isPasswordResetDataValid() {

        let data = this.passwordResetData || {};

        return (
            !!data.code
            && !!data.password
            && !!data.passwordVerification
            && data.password == data.passwordVerification
        );

    };

    isAccountDataValid() {

        let data = this.accountData || {};

        return (
            data.$changed
            && !!data.firstName
            && !!data.lastName
        );

    };

    // --------------------------------------------------------------------------------------------  

    async _prepare(options, activeRoute) {

        await super._prepare(options, activeRoute);
        if (options.mode)
            this.viewMode = options.mode;

    };

    async _refresh(options, activeRoute) {

        if (activeRoute.options && activeRoute.options.mode)
            this.viewMode = activeRoute.options.mode;

        const routeDescriptor = this.getRouteDescriptor(activeRoute.def.name);
        if (routeDescriptor) {
            if (routeDescriptor.initializer && typeof routeDescriptor.initializer == 'function') {
                routeDescriptor.initializer(this, options, activeRoute);
            }
        }

        return super._refresh(options, activeRoute);

    };

    // --------------------------------------------------------------------------------------------

    getAccountService() {

        if (!this.accountService)
            this.accountService = this.getServiceFactory().createAccountService();

        return this.accountService;

    };

    getDataManager() {

        return this.getAccountService();

    };

    async registerUser() {

        const app = this.getApp();

        app.setBusyFlag();
        try {

            await this.getDataManager().registerUser(this.registerData);
            app.routeTo('account-registration-success');

        } catch (error) {

            app.handleError(error);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    async verifyEmail() {

        const app = this.getApp();

        app.setBusyFlag();
        try {

            await this.getDataManager().verifyEmail(this.emailVerificationData)
            this.getApp().routeTo('account-email-verification-success');

        } catch (error) {

            app.handleError(error);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    async changeDetails() {

        const app = this.getApp();

        app.setBusyFlag();
        try {

            await this.getDataManager().changeDetails(this.accountData);

            const authManager = this.getAuthManager();
            await authManager.refreshUser(true);

            this.userData = this.createUserData(this);
            this.accountData = this.createAccountData(this);

        } catch (error) {

            app.handleError(error);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    async changePassword() {

        const app = this.getApp();

        app.setBusyFlag();
        try {

            await this.getDataManager().changePassword(this.passwordChangeData);
            this.passwordChangeData = this.createPasswordChangeData();
            this.getApp().showToast('success', i18n.t('module.account.msg_password_changed'));

        } catch (error) {

            app.handleError(error);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    async startPasswordReset() {

        const app = this.getApp();

        app.setBusyFlag();
        try {

            await this.getDataManager().startPasswordReset(this.passwordResetStartData);
            this.passwordResetStartData = this.createPasswordResetStartData();
            this.getApp().routeTo('account-password-reset-start-success');

        } catch (error) {

            app.handleError(error);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    async resetPassword() {

        const app = this.getApp();

        app.setBusyFlag();
        try {

            await this.getDataManager().resetPassword(this.passwordResetData);
            this.viewMode = AccountModule.MODE_PASSWORD_RESET_SUCCESS;
            this.passwordResetData = this.createPasswordResetData();

        } catch (error) {

            app.handleError(error);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    // --------------------------------------------------------------------------------------------

    _render(r, vm) {

        const module = this;

        let viewNodes = null;

        switch (module.viewMode) {
            case AccountModule.MODE_REGISTRATION_SUCCESS:
                viewNodes = renderRegistrationSuccessView();
                break;
            case AccountModule.MODE_PASSWORD_RESET_START:
                viewNodes = renderPasswordResetStartView();
                break;
            case AccountModule.MODE_PASSWORD_RESET_START_SUCCESS:
                viewNodes = renderPasswordResetStartSuccessView();
                break;
            case AccountModule.MODE_PASSWORD_RESET:
                viewNodes = renderPasswordResetView();
                break;
            case AccountModule.MODE_PASSWORD_RESET_SUCCESS:
                viewNodes = renderPasswordResetSuccessView();
                break;
            case AccountModule.MODE_EMAIL_VERIFICATION:
                viewNodes = renderEmailVerificationView();
                break;
            case AccountModule.MODE_EMAIL_VERIFICATION_SUCCESS:
                viewNodes = renderEmailVerificationSuccessView();
                break;
            case AccountModule.MODE_ACCOUNT_DETAILS:
                viewNodes = renderAccountDetailsView();
                break;
            default:
                viewNodes = renderRegistrationView();
                break;
        }

        return r.module(null, [
            r.el('div', 'space'),
            r.card({ css: 'large-width auto-horizontal-margin' }, normalizeControls(viewNodes)),
        ]);

        function normalizeControls(controls) {
            if (Array.isArray(controls))
                return controls;
            return [controls];
        }

        function renderField(label, control) {
            return r.field(null, [
                r.label([label]),
                control,
            ]);
        }

        function renderTextBox(value, props = null) {
            return r.textBox(value, { props });
        }

        function renderActionButton(label, props = null) {
            return r.textButton(label, { props });
        }

        function renderButtonLink(url, label) {
            return r.link({ url, css: 'button outlined' }, [label]);
        }

        function renderLoginLinkSection(label) {
            const app = module.getApp();
            const loginUrl = app.getRouteHash('auth-login');

            return r.section(null, [
                renderButtonLink(loginUrl, label),
            ]);
        }

        function renderRegistrationView() {

            const app = module.getApp();
            const loginUrl = app.getRouteHash('auth-login');

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.title_registration')
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.username.label'), renderTextBox(module.registerData.username, {
                                onchange: function (e) {
                                    module.registerData.username = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.email.label'), renderTextBox(module.registerData.email, {
                                onchange: function (e) {
                                    module.registerData.email = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.password.label'), renderTextBox(module.registerData.password, {
                                type: 'password',
                                onchange: function (e) {
                                    module.registerData.password = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.password_verification.label'), renderTextBox(module.registerData.passwordVerification, {
                                type: 'password',
                                onchange: function (e) {
                                    module.registerData.passwordVerification = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's6' }, [
                            renderField(i18n.t('field.first_name.label'), renderTextBox(module.registerData.firstName, {
                                onchange: function (e) {
                                    module.registerData.firstName = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's6' }, [
                            renderField(i18n.t('field.last_name.label'), renderTextBox(module.registerData.lastName, {
                                onchange: function (e) {
                                    module.registerData.lastName = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                    ]),
                    r.nav(null, [
                        renderActionButton(i18n.t('button.register.label'), {
                            disabled: !module.isRegisterDataValid(),
                            onclick: function (e) {
                                module.registerUser();
                            }
                        }),
                    ])
                ]),

                r.section(null, [
                    renderButtonLink(loginUrl, i18n.t('module.account.lbl_back_to_login_page')),
                ]),
            ];

        };

        function renderRegistrationSuccessView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.registration_title')
                    ]),
                    r.p([
                        i18n.t('module.account.registration_success')
                    ]),
                    r.p([
                        i18n.t('module.account.registration_success_msg')
                    ]),
                ]),
                renderLoginLinkSection(i18n.t('module.account.msg_proceed_to_login_page')),
            ];

        };

        function renderEmailVerificationView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.registration_title')
                    ]),
                    r.p([
                        i18n.t('module.account.registration_email_verification_msg')
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.verification_code.label'), renderTextBox(module.emailVerificationData.code, {
                                type: 'text',
                                onkeyup: function (e) {
                                    module.emailVerificationData.code = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                    ]),
                    r.nav(null, [
                        renderActionButton(i18n.t('module.account.btn_verify'), {
                            disabled: !module.isEmailVerificationDataValid(),
                            onclick: function (e) {
                                module.verifyEmail();
                            }
                        }),
                    ]),
                ]),

                renderLoginLinkSection(i18n.t('module.account.msg_proceed_to_login_page')),
            ];

        };

        function renderEmailVerificationSuccessView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.registration_title')
                    ]),
                    r.p([
                        i18n.t('module.account.registration_email_verification_sucess')
                    ]),
                    r.p([
                        i18n.t('module.account.registration_email_verification_success_msg')
                    ]),
                ]),
                renderLoginLinkSection(i18n.t('module.account.msg_proceed_to_login_page')),
            ];

        };

        function renderPasswordResetStartView() {

            const app = module.getApp();
            const loginUrl = app.getRouteHash('auth-login');

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.password_reset_title')
                    ]),
                    r.p('s12', [
                        i18n.t('module.account.password_reset_start_instructions')
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.email.label'), renderTextBox(module.passwordResetStartData.email, {
                                onkeyup: function (e) {
                                    module.passwordResetStartData.email = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                    ]),
                    r.nav(null, [
                        renderActionButton(i18n.t('module.account.btn_reset_password'), {
                            disabled: !module.isPasswordResetStartDataValid(),
                            onclick: function (e) {
                                module.startPasswordReset();
                            }
                        }),
                    ]),
                ]),
                r.p([
                    renderButtonLink(loginUrl, i18n.t('module.account.lbl_back_to_login_page')),
                ]),
            ];

        };

        function renderPasswordResetStartSuccessView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.password_reset_title')
                    ]),
                    r.p([
                        i18n.t('module.account.password_reset_start_success')
                    ]),
                    r.p([
                        i18n.t('module.account.password_reset_start_success_instructions')
                    ]),
                ]),
                renderLoginLinkSection(i18n.t('module.account.lbl_back_to_login_page')),
            ];
        };

        function renderPasswordResetView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.password_reset_title'),
                    ]),
                    r.p([
                        i18n.t('module.account.password_reset_instructions')
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.verification_code.label'), renderTextBox(module.passwordResetData.code, {
                                type: 'text',
                                onkeyup: function (e) {
                                    module.passwordResetData.code = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.password.label'), renderTextBox(module.passwordResetData.password, {
                                type: 'password',
                                onkeyup: function (e) {
                                    module.passwordResetData.password = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.password_verification.label'), renderTextBox(module.passwordResetData.passwordVerification, {
                                type: 'password',
                                onkeyup: function (e) {
                                    module.passwordResetData.passwordVerification = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                    ]),
                    r.nav(null, [
                        renderActionButton(i18n.t('module.account.btn_reset_password'), {
                            disabled: !module.isPasswordResetDataValid(),
                            onclick: function (e) {
                                module.resetPassword();
                            }
                        })
                    ]),
                ]),
                renderLoginLinkSection(i18n.t('module.account.lbl_back_to_login_page')),
            ];

        };

        function renderPasswordResetSuccessView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.password_reset_title')
                    ]),
                    r.p([
                        i18n.t('module.account.password_reset_success')
                    ]),
                    r.p([
                        i18n.t('module.account.password_reset_success_msg')
                    ]),
                ]),
                renderLoginLinkSection(i18n.t('module.account.lbl_back_to_login_page')),
            ];
        };

        function renderAccountDetailsView() {

            return [
                r.section(null, [
                    r.h4([
                        i18n.t('module.account.title_account_details'),
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.username.label'), renderTextBox(module.userData.username, {
                                readonly: '',
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.email.label'), renderTextBox(module.userData.email, {
                                readonly: '',
                            })),
                        ]),
                    ]),
                    r.h4([
                        i18n.t('module.account.title_personal_information'),
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's6' }, [
                            renderField(i18n.t('field.first_name.label'), renderTextBox(module.accountData.firstName, {
                                onkeyup: function (e) {
                                    module.accountData.firstName = e.target.value;
                                    module.accountData.$changed = true;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's6' }, [
                            renderField(i18n.t('field.last_name.label'), renderTextBox(module.accountData.lastName, {
                                onkeyup: function (e) {
                                    module.accountData.lastName = e.target.value;
                                    module.accountData.$changed = true;
                                    module.invalidate();
                                }
                            })),
                        ]),
                    ]),
                    r.nav(null, [
                        renderActionButton(i18n.t('button.save.label'), {
                            disabled: !module.isAccountDataValid(),
                            onclick: function (e) {
                                module.changeDetails();
                            }
                        }),
                    ]),
                    r.h4([
                        i18n.t('module.account.title_password_change'),
                    ]),
                    r.grid(null, [
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.actual_password.label'), renderTextBox(module.passwordChangeData.actualPassword, {
                                type: 'password',
                                onkeyup: function (e) {
                                    module.passwordChangeData.actualPassword = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.new_password.label'), renderTextBox(module.passwordChangeData.password, {
                                type: 'password',
                                onkeyup: function (e) {
                                    module.passwordChangeData.password = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                        r.cell({ css: 's12' }, [
                            renderField(i18n.t('field.new_password_verification.label'), renderTextBox(module.passwordChangeData.passwordVerification, {
                                type: 'password',
                                onkeyup: function (e) {
                                    module.passwordChangeData.passwordVerification = e.target.value;
                                    module.invalidate();
                                }
                            })),
                        ]),
                    ]),
                    r.nav(null, [
                        renderActionButton(i18n.t('module.account.btn_change_password'), {
                            disabled: !module.isPasswordChangeDataValid(),
                            onclick: function (e) {
                                module.changePassword();
                            }
                        }),
                    ]),
                ]),
                renderLoginLinkSection(i18n.t('module.account.lbl_back_to_login_page')),
            ];

        };

    };

};